import {InitPageStrict} from "../../index";

// Mode constants matching backend
const MODE_SO_SINGLE = 1;
const MODE_SO_MULTI = 2;
const MODE_SO_SINGLE_AMZ = 3;
const MODE_SO_MULTI_AMZ = 4;
const MODE_TO = 5;
const MODE_SO_SINGLE_CL = 6;
const MODE_SO_MULTI_CL = 7;
const MODE_SO_SINGLE_AMZ_CL = 8;
const MODE_SO_MULTI_AMZ_CL = 9;
const MODE_TO_CL = 10;

class WOPPickingIndex {
    constructor() {
        this.bindEvents();
    }

    /**
     * Check if two modes are compatible for merging
     * Each mode can merge with its chase list variant
     */
    private areModesCompatible(mode1: number, mode2: number): boolean {
        // Same mode
        if (mode1 === mode2) return true;

        // TO modes can merge together
        if ((mode1 === MODE_TO || mode1 === MODE_TO_CL) && 
            (mode2 === MODE_TO || mode2 === MODE_TO_CL)) {
            return true;
        }

        // SO_SINGLE modes can merge together
        if ((mode1 === MODE_SO_SINGLE || mode1 === MODE_SO_SINGLE_CL) && 
            (mode2 === MODE_SO_SINGLE || mode2 === MODE_SO_SINGLE_CL)) {
            return true;
        }

        // SO_MULTI modes can merge together
        if ((mode1 === MODE_SO_MULTI || mode1 === MODE_SO_MULTI_CL) && 
            (mode2 === MODE_SO_MULTI || mode2 === MODE_SO_MULTI_CL)) {
            return true;
        }

        // SO_SINGLE_AMZ modes can merge together
        if ((mode1 === MODE_SO_SINGLE_AMZ || mode1 === MODE_SO_SINGLE_AMZ_CL) && 
            (mode2 === MODE_SO_SINGLE_AMZ || mode2 === MODE_SO_SINGLE_AMZ_CL)) {
            return true;
        }

        // SO_MULTI_AMZ modes can merge together
        if ((mode1 === MODE_SO_MULTI_AMZ || mode1 === MODE_SO_MULTI_AMZ_CL) && 
            (mode2 === MODE_SO_MULTI_AMZ || mode2 === MODE_SO_MULTI_AMZ_CL)) {
            return true;
        }

        return false;
    }

    /**
     * Check if a mode is TO or TO_CL
     */
    private isToMode(mode: number): boolean {
        return mode === MODE_TO || mode === MODE_TO_CL;
    }

    /**
     * Get selected wave orders data
     */
    private getSelectedWaveOrders(): Array<{id: string, mode: number, ref: string | null}> {
        const selected: Array<{id: string, mode: number, ref: string | null}> = [];
        
        $('.wop:checked').each(function() {
            const $checkbox = $(this);
            const mode = parseInt($checkbox.data('mode-type') || '0', 10);
            const ref = $checkbox.data('reference-number') || null;
            const id = $checkbox.val() as string;
            
            selected.push({ id, mode, ref });
        });
        
        return selected;
    }

    /**
     * Check if all selected waves have compatible modes for merging
     */
    private canMergeSelectedWaves(): boolean {
        const selected = this.getSelectedWaveOrders();
        
        if (selected.length < 2) return false;

        // Check if all modes are compatible
        for (let i = 0; i < selected.length; i++) {
            for (let j = i + 1; j < selected.length; j++) {
                if (!this.areModesCompatible(selected[i].mode, selected[j].mode)) {
                    return false;
                }
            }
        }

        // For TO/TO_CL modes, check if all have same REF
        const toModes = selected.filter(w => this.isToMode(w.mode));
        if (toModes.length > 0) {
            // All selected waves must be TO modes if any TO mode is selected
            if (toModes.length !== selected.length) {
                return false;
            }
            
            // All TO waves must have the same REF (if they have REFs)
            const refs = toModes.map(w => w.ref).filter(r => r !== null && r !== '');
            
            // If some have REFs and some don't, they can't merge
            if (refs.length > 0 && refs.length < toModes.length) {
                return false;
            }
            
            // If all have REFs, they must be the same
            if (refs.length > 0) {
                const firstRef = refs[0];
                return refs.every(ref => ref === firstRef);
            }
            
            // If none have REFs, they can merge
            return true;
        }

        return true;
    }

    /**
     * Update merge button state based on selection
     */
    private updateMergeButtonState(): void {
        const $mergeButton = $('.merge_wave_orders');
        const canMerge = this.canMergeSelectedWaves();
        
        if (canMerge) {
            $mergeButton.prop('disabled', false);
            $mergeButton.css('opacity', '1');
            $mergeButton.css('cursor', 'pointer');
        } else {
            $mergeButton.prop('disabled', true);
            $mergeButton.css('opacity', '0.5');
            $mergeButton.css('cursor', 'not-allowed');
        }
    }

    /**
     * Validate TO/TO_CL waves for bulk delete via backend API
     * Uses the existing waves_by_reference endpoint to check if all waves with same REF are selected
     * Returns promise that resolves to error message if validation fails, null otherwise
     */
    private async validateToWavesForDelete(): Promise<string | null> {
        const selected = this.getSelectedWaveOrders();
        const toWaves = selected.filter(w => this.isToMode(w.mode));
        
        if (toWaves.length === 0) {
            return null; // No TO waves, validation passes
        }

        // Get waves_by_reference path from global variable set in the view
        const wavesByReferencePath = (window as any).wavesByReferencePath;
        if (!wavesByReferencePath) {
            console.error('wavesByReferencePath not found');
            return null; // Fallback: allow delete if path not found
        }

        // Group selected TO waves by reference number
        const refGroups: {[ref: string]: Array<{id: string, mode: number, ref: string | null}>} = {};
        
        toWaves.forEach(wave => {
            const ref = wave.ref;
            if (ref && ref !== '') {
                if (!refGroups[ref]) {
                    refGroups[ref] = [];
                }
                refGroups[ref].push(wave);
            }
        });

        // Check each reference group
        for (const ref in refGroups) {
            const selectedWavesWithRef = refGroups[ref];
            const selectedIds = selectedWavesWithRef.map(w => w.id);

            try {
                // Call waves_by_reference endpoint to get ALL waves with this REF
                const response = await $.ajax({
                    url: wavesByReferencePath,
                    method: 'GET',
                    data: { reference_number: ref },
                    dataType: 'json'
                });

                if (response.error) {
                    console.error('Error fetching waves by reference:', response.error);
                    continue; // Skip this REF, allow delete to proceed
                }

                // Get all wave IDs from the response
                const allWaveIds = response.wave_ids || [];
                
                // Check if all waves with this REF are selected
                const selectedIdsSet = new Set(selectedIds.map(id => String(id)));
                const allIdsSet = new Set(allWaveIds.map((id: number) => String(id)));
                
                // Check if selectedIdsSet contains all IDs from allIdsSet
                const allSelected = Array.from(allIdsSet).every((id: string) => selectedIdsSet.has(id));
                
                if (!allSelected) {
                    const totalCount = allWaveIds.length;
                    return `For delete TO#${ref}, you should select all ${totalCount} wave(s) with same REF`;
                }
            } catch (error) {
                console.error('Error validating delete for REF:', ref, error);
                // On error, continue checking other REFs
                continue;
            }
        }

        return null; // Validation passed
    }

    private bindEvents(): void {
        // Select all functionality
        $('#select_all').on('change', () => {
            const checkAll = $('#select_all').is(':checked');
            $('.wop:enabled').prop("checked", checkAll);
            this.updateMergeButtonState();
        });

        // Individual checkbox change handler
        $(document).on('change', '.wop', () => {
            const selectedCount = $('.wop:checked').length;
            const enabledCount = $('.wop:enabled').length;

            if (enabledCount === 0) {
                $('#select_all').prop('checked', false);
                this.updateMergeButtonState();
                return;
            }

            if (selectedCount === enabledCount) {
                $('#select_all').prop('checked', true);
            } else {
                $('#select_all').prop('checked', false);
            }

            this.updateMergeButtonState();
        });

        // Bulk delete button click handler - validate before opening modal
        // Use event delegation with higher priority to run before the delete modal handler
        $(document).on('click', '.delete_wave_orders', async (e) => {
            // Check if any wave orders are selected
            const checkedBoxes = $('input[name="wo_picking[]"]:checked');
            if (checkedBoxes.length === 0) {
                e.preventDefault();
                e.stopPropagation();
                if (typeof (window as any).swal !== 'undefined') {
                    (window as any).swal({
                        text: 'Please select at least one wave order to delete.',
                        icon: 'warning'
                    });
                }
                return false;
            }

            // Validate TO/TO_CL waves via backend
            const validationError = await this.validateToWavesForDelete();
            if (validationError) {
                e.preventDefault();
                e.stopPropagation();
                
                if (typeof (window as any).swal !== 'undefined') {
                    (window as any).swal({
                        text: validationError,
                        icon: 'warning'
                    });
                } else {
                    alert(validationError);
                }
                return false;
            }

            // Validation passed - open the modal directly
            e.preventDefault();
            e.stopPropagation();
            
            // Update selected count display
            $('#selected-wave-count-delete').text(checkedBoxes.length + ' wave order(s) selected');
            
            // Populate hidden field with selected wave order IDs
            const selectedIds: string[] = [];
            
            checkedBoxes.each(function() {
                selectedIds.push($(this).val() as string);
            });
            
            // Clear any existing hidden fields in the modal form and add new ones
            const modalForm = $('#deleteWaveOrdersModal form');
            modalForm.find('input[name="wo_picking[]"]').remove();
            
            // Add hidden fields for each selected ID
            selectedIds.forEach(function(id) {
                modalForm.append('<input type="hidden" name="wo_picking[]" value="' + id + '">');
            });

            // Update confirmation message
            const waveOrderCount = selectedIds.length;
            if (waveOrderCount > 0) {
                const message = 'Are you sure you want to delete ' + waveOrderCount + ' wave order(s)? This will also revert associated orders to "Created" status.';
                $('#confirmation-message-text-delete').text(message);
            }
            
            $('#deleteWaveOrdersModal').modal('show');
            
            return false;
        });

        // Merge button click handler - validate before opening modal
        $(document).on('click', '.merge_wave_orders', (e) => {
            // Check if button is disabled (handled by updateMergeButtonState)
            if ($(e.target).prop('disabled') || $(e.target).closest('button').prop('disabled')) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }

            const selected = this.getSelectedWaveOrders();
            
            if (selected.length < 2) {
                e.preventDefault();
                e.stopPropagation();
                
                if (typeof (window as any).swal !== 'undefined') {
                    (window as any).swal({
                        text: 'You should select at least 2 wave orders to merge.',
                        icon: 'warning'
                    });
                }
                return false;
            }

            if (!this.canMergeSelectedWaves()) {
                e.preventDefault();
                e.stopPropagation();
                
                const toModes = selected.filter(w => this.isToMode(w.mode));
                let errorMsg = 'Cannot merge selected wave orders. ';
                
                if (toModes.length > 0) {
                    // Check if it's a REF issue or mode issue
                    const refs = toModes.map(w => w.ref || '').filter(r => r !== '');
                    if (refs.length > 0) {
                        const uniqueRefs: string[] = [];
                        refs.forEach(ref => {
                            if (uniqueRefs.indexOf(ref) === -1) {
                                uniqueRefs.push(ref);
                            }
                        });
                        if (uniqueRefs.length > 1) {
                            errorMsg += 'For TO/TO_CL modes, all selected waves must have the same REF number.';
                        } else if (selected.length !== toModes.length) {
                            errorMsg += 'Cannot mix TO/TO_CL modes with other modes.';
                        } else {
                            errorMsg += 'For TO/TO_CL modes, all selected waves must have the same REF number.';
                        }
                    } else {
                        errorMsg += 'Selected waves have incompatible modes.';
                    }
                } else {
                    errorMsg += 'Selected waves have incompatible modes.';
                }
                
                if (typeof (window as any).swal !== 'undefined') {
                    (window as any).swal({
                        text: errorMsg,
                        icon: 'warning'
                    });
                } else {
                    alert(errorMsg);
                }
                return false;
            }
            
            // Validation passed - allow the default behavior (modal opening)
        });

        // Initial state update
        this.updateMergeButtonState();
    }
}

declare global {
    interface Window {
        WOPPickingIndex: WOPPickingIndex;
    }
}

export function LoadPage(): void {
    InitPageStrict("wop pickings index", () => {
        const wopPickingIndex = new WOPPickingIndex();
        window.WOPPickingIndex = wopPickingIndex;
    });
}
